import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CellClickEvent, RemoveEvent } from '@progress/kendo-angular-grid';
import { FileInfo, FileRestrictions, UploadComponent } from '@progress/kendo-angular-upload';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { DestroyBase } from '../../../../components/common/ng-destroy-base';
import { ImportPreviewRenderService } from '../../../../components/common/services/import-preview-render-service';
import { RefListModel } from '../../../../models/ref-list-model';
import { CalculationModelOverview } from '../../../app-settings/models/calculation-model-overview';
import { CurrencyManagementModel } from '../../models/currency-management.model';
import { ImportConfigurationEnum } from '../../models/enums/importconfigurationenum';
import { ImportGroupEnum, ImportGroupMapping } from '../../models/enums/importgroupenum';
import { LookupModel } from '../../models/lookup.model';
import { UnitOfLengthModel } from '../../models/unit-of-length.model';
import { CurrencyService } from '../../services/currency.service';
import { GuidService } from '../../services/guid.service';
import { ImportService } from '../../services/import.service';
import { UnitOfLengthService } from '../../services/unit-of-length.service';
import { AppServiceHelperService } from '../../../../services/app-service-helper.service';
import { FileDownloadService } from '../../../../services/file-download-service.service';
import { EventBusService } from '../../../../services/event-bus.service';
import { AppStorageService } from '../../../../services/app-storage.service';

/**
 * Import wizard for ImportV2
 */
@Component({
  selector: 'import-wizard',
  templateUrl: './import-wizard.component.html',
  styleUrls: ['./import-wizard.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ImportWizardComponent extends DestroyBase implements OnInit {
  /**
   * The Oid
   */
  @Input() oid: any;

  /**
   * Project ID
   */
  @Input() projectId: number;

  /**
   * Cloas epane event
   */
  @Output() closePane: EventEmitter<any> = new EventEmitter();

  /**
   * Number of steps
   */
  public stepsCount = 4;

  /**
   * Current step
   */
  currentStep = 1;

  /**
   * Unit of length empty?
   */
  public hasEmptyUnitOfLength = false;

  /**
   * Should the dialog be closed?
   */
  public shouldClose = false;

  /**
   * The Kendo upload component for uploading the file
   */
  @ViewChild("kendoUpload") kendoUpload: UploadComponent;

  /**
   * List of units of length
   */
  unitOfLengthList: UnitOfLengthModel[] = [];

  /**
   * List of currencies
   */
  currencies: CurrencyManagementModel[] = [];

  /**
   * List of calculation model templates
   */
  calculationModelTemplatesList: CalculationModelOverview[] = [];

  /**
   * Were all templates selected?
   */
  allTemplatesFilled = false;

  /**
   * Import groups
   */
  importGroups = ImportGroupMapping;

  /**
   * Mapping versions
   */
  mappingVersions: LookupModel[] = [];

  /**
   * Import group config
   */
  importGroupConfiguration: any;

  /**
   * Form group
   */
  formGroup: UntypedFormGroup;

  /**
   * Files form group
   */
  filesFormGroup: UntypedFormGroup = new UntypedFormGroup({});

  /**
   * Properties available for import
   */
  propertiesForImport: RefListModel[] = [];

  /**
   * We will hold here all preview rows that need property assigned, so we can check if all properties are filled
   */
  propertyPreviewRows = [];

  /**
   * All properties assigned?
   */
  allPropertiesAssigned = false;

  /**
   * URL for uploading
   */
  uploadSaveUrl: string;

  /**
   * Show the currency selection section?
   */
  showCurrencySection = true;

  /**
   * Show the upload section?
   */
  showUploadSection = false;

  /**
   * Show the uploaded files section?
   */
  showUploadedFiles = false;

  /**
   * Show the import data preview grid section?
   */
  showPreviewGrids = false;

  /**
   * The import preview data
   */
  previewData: any = null;

  /**
   * Currency symbol
   */
  currencySymbol: string;

  /**
   * The import GUIDs
   */
  importGuids: string[] = [];

  /**
   * The import group ID
   */
  importGroupId: number = null;

  /**
   * Import validation result
   */
  validationResult = null;

  /**
   * Is the user an admin?
   */
  isAdmin = false;

  /**
   * The uploaded files
   */
  uploadedFiles: any[] = [];

  /**
   * Files count
   */
  private filesCount = 0;// track files count because Kendo calling import events for each file

  /**
   * Maximum number of files that can be uploaded
   */
  private readonly maxFilesCount = 10;//Bug 19171: Importprocess : Step 3 stuck while Import of many files

  /**
   * Maximum request size: 50MB
   */
  private maximumRequestSize = 50 * 1024 * 1024;

  /**
   * File upload restrictions for kendo upload
   */
  public uploadRestrictions: FileRestrictions = {
    maxFileSize: this.maximumRequestSize
  };

  /**
   * Empty entry for properties
   */
  private emptyPropertyEntry = {
    "calculationModelId": 0,
    "oid": 0,
    "parentNo": null,
    "description": "",
    "additionalDescription": null
  };

  /**
   * Step 2 enabled?
   */
  private step2Enabled = false;

  /**
   * Constructor
   * @param _currencyService
   * @param _unitOfLengthService
   * @param appStorage
   * @param eventBus
   * @param _importService
   * @param http
   * @param guidService
   * @param iprs
   * @param appServiceHelper
   * @param fileDownloadService
   */
  constructor(private _currencyService: CurrencyService,
    private _unitOfLengthService: UnitOfLengthService,
    private appStorage: AppStorageService,
    private eventBus: EventBusService,
    private _importService: ImportService,
    private http: HttpClient,
    private guidService: GuidService,
    public iprs: ImportPreviewRenderService,
    private appServiceHelper: AppServiceHelperService,
    private fileDownloadService: FileDownloadService) {
    super();
  }

  //#region Steps handling

  /**
   * Close was clicked
   */
  public onCloseClicked(): void {
    this.shouldClose = true;
    setTimeout(() => {
      this.closePane.emit();
    }, 500);//let animation finish
  }

  /**
   * Go one step back
   */
  public prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep -= 1;
    }
  }

  /**
   * Check if the next step is enabled
   */
  public isNextEnabled(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.step2Enabled;
      case 2:
        return this.allTemplatesFilled;
      case 3:
        return this.uploadedFiles.length > 0;
      default:
        return false;
    }
  }

  /**
   * Proceed to the next step
   */
  public async nextStep(): Promise<void> {
    if (this.currentStep >= this.stepsCount) {
      return;
    }

    let success: boolean;

    switch (this.currentStep) {
      case 1:
        this.currentStep += 1;
        break;
      case 2:
        success = this.uploadedFiles.length > 0;
        if (!success) {
          success = await this.onUploadFiles();
        }

        this.currentStep += (success ? 1 : 0);
        break;
      case 3:
        success = this.previewData !== null ? true : await this.startPreuploadedImportPreview();
        this.hasEmptyUnitOfLength = this.checkEmptyUnitOfLength(this.previewData?.data);
        this.currentStep += (success ? 1 : 0);
        break;
      case 4:
        //nothing to do
        break;
      default:
        break;
    }
  }

  /**
   * Check if the unit of length is empty
   * @param data
   * @returns
   */
  private checkEmptyUnitOfLength(data: any): boolean {
    if (!data) {
      return false;
    }

    const filledImportKeys = Object.keys(data).filter((key) => data[key].DataRoot?.Data?.length);
    for (const key of filledImportKeys) {
      for (const dataSet of data[key]?.DataRoot?.Data as any[]) {
        if (dataSet['UnitOfLengthOverride'] === null) {
          return true;
        }
      }
    }
    return false;
  }

  //#endregion

  /**
   * Enable the next action on initial load
   */
  private enableInitialNextAction(): void {
    //enable only when all data is ready
    this.step2Enabled =
      this.currencies.length //all currencies loaded
      && this.unitOfLengthList.length //units of length loaded
      && this.importGroupConfiguration; //import groups config loaded
  }

  /**
   * On component init
   */
  ngOnInit(): void {
    this.isAdmin = this.appStorage.userIsInRole(['Admin']);
    this.kendoUpload?.clearFiles();

    this.eventBus.broadcastEvent('enableNextImportAction', false);

    this._currencyService.loadDefinedCurrenciesForImport(this.oid).subscribe(data => {
      this.currencies = data;

      this.enableInitialNextAction();
    });

    this._unitOfLengthService.loadUnitsOfLength().subscribe(data => {
      this.unitOfLengthList = data;
      this.enableInitialNextAction();
    });

    this.subscriptions.add(
      combineLatest([
        this._importService.getDefinedCalculationModelTemplatesForImport(this.oid),
        this._importService.getImportConfigurationForProject(this.projectId)
      ]).subscribe(([templatesList, config]) => {
        /*
         dropdown with names of all templates (sorted alphabetically):
         - that are chosen for properties selected for the VE (Product Backlog Item 13198: Import enhancements - Apply registered Calculation Model Template)
         - plus a "virtual" entry "Flat File")
        */
        this.calculationModelTemplatesList = templatesList;

        const flatFileEntry = {
          id: -1,
          name: 'Flat File'
        };
        this.calculationModelTemplatesList.push(flatFileEntry);

        this.importGroupConfiguration = config.importGroups;
        if (config.projectImportConfigurationId == ImportConfigurationEnum.Sal) {
          this.importGroups = this.importGroups.filter(g => g.type === ImportGroupEnum.FlatFile);
        } else if (config.projectImportConfigurationId == ImportConfigurationEnum.Barings) {
          this.importGroups = this.importGroups.filter(g => g.type === ImportGroupEnum.BaringsFlatFile || g.type === ImportGroupEnum.Dcf);
        } else if (config.projectImportConfigurationId == ImportConfigurationEnum.Axa) {
          this.importGroups = this.importGroups.filter(g => g.type === ImportGroupEnum.AxaFlatFile || g.type === ImportGroupEnum.AxaDcf);
        } else if (config.projectImportConfigurationId == ImportConfigurationEnum.France) {
          this.importGroups = this.importGroups.filter(g => g.type === ImportGroupEnum.FranceDcf);

          this.calculationModelTemplatesList = this.calculationModelTemplatesList.filter((entry) => { return entry.id !== flatFileEntry.id; });//No FlatFile in France
        } else if (config.projectImportConfigurationId == ImportConfigurationEnum.OneReport) {
          this.importGroups = this.importGroups.filter(g => g.type === ImportGroupEnum.OneReportDcf);

          this.calculationModelTemplatesList = this.calculationModelTemplatesList.filter((entry) => { return entry.id !== flatFileEntry.id; });//No FlatFile in OneReportDcf
        } else if (config.projectImportConfigurationId == ImportConfigurationEnum.UsReport) {
          this.importGroups = this.importGroups.filter(g => g.type === ImportGroupEnum.UsReport);

          this.calculationModelTemplatesList = this.calculationModelTemplatesList.filter((entry) => { return entry.id !== flatFileEntry.id; });//No FlatFile in UsReport
        }

        config.mappingVersions.forEach(version => {
          const newVersion = new LookupModel();
          newVersion.id = version.oid;
          newVersion.name = version.description
          this.mappingVersions.push(newVersion);
        });

        this.enableInitialNextAction();
      }));

    this.subscriptions.add(this._importService.getPropertiesForImport(this.oid).subscribe(data => {
      //properties on valuation
      this.propertiesForImport = data;
    }));
  }

  /**
   * Handler for the cell edit event
   * @param sender The sender of the event
   * @param rowIndex The index of the row
   * @param columnIndex The index of the column
   * @param dataItem The data item associated with the cell
   */
  public cellEditHandler({ sender, rowIndex, columnIndex, dataItem }: CellClickEvent): void {
    if (columnIndex === 1) { // Default unit of length of lead currency can now be edited
      this.formGroup = this.createFormGroup(dataItem);
      sender.editCell(rowIndex, columnIndex, this.formGroup);
    }
  }

  /**
   * Creates a form group for the given data item
   * @param dataItem The data item for which to create the form group
   * @returns The created form group
   */
  createFormGroup = (dataItem) =>
    new UntypedFormGroup({
      ValueInBaseCurrency: new UntypedFormControl(dataItem.ValueInBaseCurrency),
      DefaultUnitOfLengthId: new UntypedFormControl(dataItem.DefaultUnitOfLengthId, Validators.required),
    });

  /**
   * Handles the change of the unit of length for a currency
   * @param newValue The new value of the unit of length
   * @param item The currency management model item
   */
  public unitOfLengthChanged(newValue: number, item: CurrencyManagementModel) {
    item.DefaultUnitOfLengthId = +newValue;
    item.DefaultUnitOfLength = this.unitOfLengthList.find(i => i.oid === +newValue).description;
  }

  /**
   * Reads a file and returns a promise with the file information
   * @param file The file to read
   * @param calculationModelTemplateId The calculation model template ID
   * @returns A promise that resolves with the file information
   */
  async readFile(file: File, calculationModelTemplateId: number): Promise<any> {
    return new Promise<any>((resolve) => {
      const fileReader: FileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onloadend = (e) => {
        const dataUrl = e.target.result;
        resolve({
          fileName: file.name,
          dataUrl,
          calculationModelTemplateId,
          size: file.size
        });
      }
    });
  }

  /**
   * Removes a file from the upload component and updates the uploaded files list
   * @param upload The upload component
   * @param uid The unique ID of the file to remove
   */
  public onRemoveFile(upload: UploadComponent, uid: string): void {
    this.filesCount--;
    this.uploadedFiles = [];
    upload.removeFilesByUid(uid);
    this.filesFormGroup.removeControl(uid);
    setTimeout(() => this.checkAllTemplatesFilled());
  }

  /**
   * Clears all files from the upload component and updates the uploaded files list
   */
  public onClearFiles(): void {
    this.filesCount = 0;
    this.uploadedFiles = [];
    setTimeout(() => this.checkAllTemplatesFilled());
  }

  /**
   * Handles the selection of files by adding them to the upload component
   * @param e The file select event
   */
  public onFileSelect(e): void {
    e.preventDefault();

    let chosenFilesCount = e.files.length;

    if (this.filesCount + chosenFilesCount > this.maxFilesCount) {
      //restrict count of files that can be uploaded in one import
      chosenFilesCount = this.maxFilesCount - this.filesCount;

      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'maximumImportFilesCount');
    }

    this.filesCount += chosenFilesCount;
    for (let i = 0; i < chosenFilesCount; ++i) {
      const file = e.files[i];
      file.uid = this.guidService.generate();
      this.filesFormGroup.addControl(file.uid, new UntypedFormControl(null));
      setTimeout(() => {
        if (!this.kendoUpload.fileList.has(file.uid)) {
          this.kendoUpload.fileList.add(file);
        }

        this.uploadedFiles = [];
        this.checkAllTemplatesFilled();
      }, (i + 1) * 10);
    }
  }

  /**
   * Checks if all template fields are filled for the uploaded files
   */
  private checkAllTemplatesFilled(): void {
    const templateIds = [];
    for (const fileArr of this.kendoUpload.fileList.files) {
      const file: FileInfo = fileArr[0];
      const templateId = this.filesFormGroup.controls[file.uid].value;
      templateIds.push(templateId);
    }

    this.allTemplatesFilled = templateIds.length > 0 && templateIds.filter(o => o === null).length === 0;
  }

  /**
   * Handles the change event of the template dropdown
   */
  public templateDropdownChanged(): void {
    this.uploadedFiles = [];
    this.checkAllTemplatesFilled();
  }

  /**
   * Upload files
   * @returns
   */
  public async onUploadFiles(): Promise<boolean> {
    const upload = this.kendoUpload;
    if (upload.fileList.files && upload.fileList.files.length === 0) {
      return false;//no files for upload
    }

    if (this.filesCount != upload.fileList.files.length) {
      return false;//skip other calls (kendo calls onImportUpload for every file added)
    }

    const importFiles: any[] = [];
    for (const fileArr of upload.fileList.files) {
      const file: FileInfo = fileArr[0];
      const calculationModelTemplateId = this.filesFormGroup.controls[file.uid].value;
      const fileObj = await this.readFile(file.rawFile, calculationModelTemplateId);
      importFiles.push(fileObj);
    }

    //reset preview & validation result
    this.validationResult = null;
    this.previewData = null;
    const chunksGuid = this.guidService.generate();

    this.eventBus.broadcastEvent('project.valuation.showLoader', true);
    for (const fileArr of upload.fileList.files) {
      await this.uploadFile(chunksGuid, fileArr[0]);
    }

    for (const file of importFiles) {
      file.dataUrl = null;
      file.chunked = true;
      file.importGuid = chunksGuid;
    }

    const url = `import/v2/import/uploadfiles?referenceBoOid=${this.oid}&asynchronous=${environment.importAsync}`;


    return new Promise((resolve) => {
      this.http.post(this.appServiceHelper.getServiceEndpoint('pollux.data.transformation.api', url), { importFiles: importFiles })
        .pipe(take(1)).subscribe({
          next: (result: any) => {
            this.eventBus.broadcastEvent('project.valuation.showLoader', false);
            this.uploadedFiles = result.data.map(
              (entry) => ({
                ...entry,
                isSelected: true,
                canTransform: entry.groupId === 5 || entry.groupId === 7 || entry.groupId === 8,  /*AxaOther, OneReport, UsReport*/
                groupDescription: this.importGroups.find(g => g.type === entry.groupId).value,
                mappingVersionDescription: this.mappingVersions.find(m => m.id === entry.mappingVersionId).name
              })
            );

            resolve(true);//will switch to next step
          },
          error: () => {
            this.eventBus.broadcastEvent('project.valuation.showLoader', false);
            resolve(false);
          }
        });
    });
  }



  private async uploadFile(chunksGuid, targetFile) {
    // create array to store the buffer chunks  
    const FileChunk = [];
    // the file object itself that we will work with  
    const file = targetFile.rawFile;
    // set up other initial vars  
    const MaxFileSizeMB = 5;
    const BufferChunkSize = MaxFileSizeMB * (1024 * 1024);
    let FileStreamPos = 0;
    // set the initial chunk length  
    let EndPos = BufferChunkSize;
    const Size = file.size;

    // add to the FileChunk array until we get to the end of the file  
    while (FileStreamPos < Size) {
      // "slice" the file from the starting position/offset, to  the required length  
      FileChunk.push(file.slice(FileStreamPos, EndPos));
      FileStreamPos = EndPos; // jump by the amount read  
      EndPos = FileStreamPos + BufferChunkSize; // set next chunk length  
    }
    // get total number of "files" we will be sending  
    const TotalParts = FileChunk.length;
    let PartCount = 0;
    let chunk: any = null;
    // loop through, pulling the first item from the array each time and sending it  
    while ((chunk = FileChunk.shift())) {
      PartCount++;
      // file name convention  
      const FilePartName = file.name + ".part_" + PartCount + "." + TotalParts;
      // send the file  
      await this.UploadFileChunk(chunksGuid, chunk, FilePartName);
    }
  }


  private UploadFileChunk(chunksGuid: string, chunk: any, fileName: string): Promise<void> {
    return new Promise((resolve) => {
      const urlChunked = `import/v2/import/uploadfiles/chunked?guid=${chunksGuid}`;
      const FD = new FormData();
      FD.append('file', chunk, fileName);

      const headers = new HttpHeaders({ 'enctype': 'multipart/form-data' });

      this.http.post(this.appServiceHelper.getServiceEndpoint('pollux.data.transformation.api', urlChunked), FD, { headers: headers })
        .pipe(take(1)).subscribe({
          next: (result: any) => {
            resolve();
          },
          error: () => {
            this.eventBus.broadcastEvent('project.valuation.showLoader', false);
            resolve();
          }
        });
    });

    /*
        return new Promise<void>((resolve) => {
    
    
          const urlChunked = `import/v2/import/uploadfiles/chunked?guid=${chunksGuid}`;
          /* var FD = new FormData();
           FD.append('file', Chunk, FileName);
           $.ajax({
             type: "POST",
             url: 'http://localhost:8170/Home/UploadFile/',
             contentType: false,
             processData: false,
             data: FD
           });
          resolve();
        });*/
  }



  /**
  * Downloads a file item
  * @param item The file item to download
  */
  public downloadFileItem(item): void {
    const url = `import/v2/import/downloadfile/${item.fileImportGuid}`;
    const downloadUrl = this.appServiceHelper.getServiceEndpoint('pollux.data.transformation.api', url);
    this.fileDownloadService.initiateDownload(downloadUrl, null, false);
  }

  /**
   * Handles the removal of an uploaded file
   * @param args The remove event arguments
   */

  public onRemoveUploadedFile(args: RemoveEvent): void {
    const item = args.dataItem;
    const url = `import/v2/import/removefile/${item.fileImportGuid}`;

    this.eventBus.broadcastEvent('project.valuation.showLoader', true);
    this.http.post(this.appServiceHelper.getServiceEndpoint('pollux.data.transformation.api', url), { item })
      .pipe(take(1)).subscribe({
        next: (gpr: any) => {
          this.eventBus.broadcastEvent('project.valuation.showLoader', false);
          if (gpr.success) {
            this.uploadedFiles = this.uploadedFiles.filter((entry) => (entry.fileImportGuid != args.dataItem.fileImportGuid));
          }
        },
        error: () => {
          this.eventBus.broadcastEvent('project.valuation.showLoader', false);
        }
      });
  }

  /**
   * Start the pre-uploaded import preview
   * @returns
   */
  private startPreuploadedImportPreview(): Promise<boolean> {
    //files are already uploaded
    //let's see what we have in grid still (user can remove some files)
    const importFiles = this.uploadedFiles;
    const importPreviewRequest = {
      currencies: this.currencies,
      importFiles
    };

    const url = `import/v2/import/prepare/uploaded?referenceBoOid=${this.oid}&autoFixErrors=false&asynchronous=${environment.importAsync}`;

    this.eventBus.broadcastEvent('project.valuation.showLoader', true);

    return new Promise((resolve) => {
      this.http.post(this.appServiceHelper.getServiceEndpoint('pollux.import.api', url), importPreviewRequest)
        .pipe(take(1)).subscribe({
          next: (result) => {
            this.eventBus.broadcastEvent('project.valuation.showLoader', false);

            this.onImportPreviewSuccess(result);
            resolve(true);
          },
          error: () => {
            this.eventBus.broadcastEvent('project.valuation.showLoader', false);
            resolve(false);
          }
        });
    });
  }

  /**
  * Handles the success of the import preview
  * @param response The response from the import preview
  */
  private onImportPreviewSuccess(response: any): void {
    this.previewData = null;
    this.importGuids = [];
    if (response?.success) {
      this.validationResult = null;

      this.previewData = this.processPreviewResponse(response);
      this.importGuids = response.importGuids;
      this.currencySymbol = response.currencyOnProjLevel;
    }
    else {
      this.validationResult = response.validationResult;
    }
  }

  /**
   * Processes the import preview response data
   * @param response The response from the import preview
   * @returns The processed preview data
   */
  private processPreviewResponse(response: any): any {
    const data = response.data;
    // Remove empty imports (probably no data in import excel)
    const emptyImportsKeys = Object.keys(data).filter((key) => !data[key].DataRoot?.Data?.length);
    emptyImportsKeys.forEach(key => delete data[key]);
    return this.injectPropertyDropdownColumns(response);
  }

  /**
   * Add the columns for property selection
   * @param response
   * @returns
   */
  private injectPropertyDropdownColumns(response: any): any {
    // #13386: we need a dropdown for every Property (on the Property Level Import sheet), that is not a Flat File, which shows all properties that are selected for the Valuation event we are importing into.
    // Label " Target Property"

    const data = response.data;
    this.processPropertyLevelData(data);
    //For FlatFiles, we need to identify the summary row
    const distinctImportGuid = this.propertyPreviewRows.filter((entry) => { return entry.isFlatFile; }).map((entry) => { return entry.ImportGuid; }).filter((thing, i, arr) => {
      return arr.indexOf(arr.find(t => t.id === thing.id)) === i;
    });

    for (const importGuid of distinctImportGuid) {
      //The summary is the last row, so go from the end. Find the first row mtching the import guid and mark it
      for (let i = this.propertyPreviewRows.length - 1; i >= 0; --i) {
        if (this.propertyPreviewRows[i].isFlatFile && this.propertyPreviewRows[i].ImportGuid == importGuid) {
          this.propertyPreviewRows[i].TargetPropertyId = -1;//add new "row object property".. dropdown will used here
          this.propertyPreviewRows[i].selectableProperties = [{
            "calculationModelId": -1,
            "oid": -1,
            "parentNo": null,
            "description": "FlatFile summary",
            "additionalDescription": null
          }];
          break; //Next importGuid - if any
        }
      }
    }
    this.checkAllPropertiesAssigned();

    return response;
  }

  /**
   * Process data at property level
   * @param data
   */
  private processPropertyLevelData(data: any): void {
    for (const sheetKey of Object.keys(data)) {
      if (sheetKey.indexOf('PropertyLevelImport') !== -1) {
        this.processPropertyLevelImportSheetData(data, sheetKey);
      }
    }
  }

  /**
   * Process import sheet data at property level
   * @param data
   * @param sheetKey
   */
  private processPropertyLevelImportSheetData(data: any, sheetKey: string): void {
    const rows = data[sheetKey].DataRoot.Data;
    for (const row of rows) {
      const importedFiles = this.uploadedFiles.filter((file) => { return file.CalculationModelTemplateId >= 0 && file.fileImportGuid === row.ImportGuid; }); //Only non-FlatFiles
      if (importedFiles?.length) {
        const importedFile = importedFiles[0];

        const properties = this.propertiesForImport.filter((property) => { return property.calculationModelId === importedFile.CalculationModelTemplateId; });
        row.selectableProperties = properties;

        if (properties && properties.length === 1) {
          row.TargetPropertyId = properties[0].oid; //add new "row object property".. dropdown will used here
        }
        else {
          row.TargetPropertyId = 0; //add new "row object property".. dropdown will used here
        }

        properties.splice(0, 0, this.emptyPropertyEntry);
      }
      else {
        row.isFlatFile = true;
        row.selectableProperties = this.propertiesForImport; //.filter((property) => { return !property.calculationModelId; });
        row.TargetPropertyId = 0; //add new "row object property".. dropdown will used here
      }

      this.propertyPreviewRows.push(row);
    }
  }

  /**
   * Checks if all properties have been assigned in the preview
   */
  private checkAllPropertiesAssigned(): void {
    this.allPropertiesAssigned = this.propertyPreviewRows.filter(row => row.TargetPropertyId === this.emptyPropertyEntry.oid).length === 0;
  }

  /**
   * Handles the change event when a property is modified in the preview
   */
  public previewPropertyChanged(): void {
    this.checkAllPropertiesAssigned();
  }

  /**
   * Continues the import process
   */
  public continueImport(): void {
    //check if have all properties assigned
    const allAssigned: boolean = this.propertyPreviewRows.filter(row => row.TargetPropertyId === this.emptyPropertyEntry.oid).length === 0;

    if (allAssigned) {
      this.runImport();
    } else {
      // #13857: ask user if ok to continue
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          setTimeout(() => {
            this.runImport();
          })
        },
        cancel: () => {
          //do nothing: just to show Cancel button
        }
      }, 'shouldContinueImportWithNotAllPropertiesAssigned');
    }
  }

  /**
   * Runs the import process
   */
  private runImport(): void {
    const promises = [];

    const data = {
      targetProperties: []
    };

    // #13857: only rows with assigned property are imported
    const propertiesForImport = this.propertyPreviewRows.filter(row => row.TargetPropertyId != 0);

    if (propertiesForImport.length === 0) {
      return;
    }

    data.targetProperties = propertiesForImport.map(row => {
      return {
        oid: row.oid,
        propertyId: row.TargetPropertyId
      };
    }) || [];

    //only import rows with property selected
    const guidsForImport = propertiesForImport.map((entry) => entry.ImportGuid);

    //to be sure take distinct guids not to repeat same import
    const distinctGuidsForImport = guidsForImport.filter((thing, i, arr) => {
      return arr.indexOf(arr.find(t => t === thing)) === i;
    });

    for (const guid of distinctGuidsForImport) {
      promises.push(new Promise<void>((resolve) => {
        this.http.post(this.appServiceHelper.getServiceEndpoint('pollux.import.api', `import/v2/import/complete?importGuid=${guid}`), data).pipe(take(1)).subscribe(() => {
          resolve();
        });
      }));
    }

    Promise.all(promises).then(() => {
      this.eventBus.broadcastEvent('project.valuation.showLoader', false);
      this.eventBus.broadcastEvent('onImportSuccess', this.oid);
      setTimeout(() => {
        //close import form
        this.onCloseClicked();
      });
    });
  }
}
